import React, { useState } from 'react';
import { PlayCircle, Hourglass } from 'lucide-react';
import blogData from '../data/blog.json';

const Blog = () => {
  const { description, posts } = blogData;
  const [modalContent, setModalContent] = useState(null);

  const openModal = (type, content) => {
    setModalContent({ type, content });
  };

  const closeModal = () => {
    setModalContent(null);
  };

  const renderModal = () => {
    if (!modalContent) return null;

    return (
      <div
        onClick={closeModal}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0,0,0,0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#000',
            padding: '1rem',
            borderRadius: '8px',
            position: 'relative',
            maxWidth: '95vw',
            maxHeight: '95vh',
            width: '1200px',
            height: '675px',
          }}
        >
          <button
            onClick={closeModal}
            style={{
              position: 'absolute',
              top: '10px',
              right: '15px',
              fontSize: '2rem',
              background: 'transparent',
              color: '#fff',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            ×
          </button>

          {modalContent.type === 'video' ? (
            <iframe
              width="1200"
              height="675"
              src={modalContent.content.replace('watch?v=', 'embed/')}
              title="YouTube Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{ width: '100%', height: '100%', maxWidth: '1200px', maxHeight: '90vh' }}
            />
          ) : (
            <img
              src={modalContent.content}
              alt="Full Preview"
              style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '6px' }}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem', fontFamily: 'sans-serif' }}>
      <h2 style={{ marginBottom: '0.25rem' }}>Blog Concepts & Assets</h2>
      <p style={{ fontSize: '1rem', color: '#555', marginBottom: '1.5rem', maxWidth: '900px' }}>
        {description}
      </p>

      {posts.map((post) => (
        <div
          key={post.id}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '2rem',
            marginBottom: '3rem',
            border: '1px solid #ddd',
            borderRadius: '10px',
            padding: '1.5rem',
            backgroundColor: '#fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          }}
        >
          {/* LEFT: Linked Video Assets */}
          <div style={{ flex: '1 1 320px', maxWidth: '400px' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#666', marginBottom: '0.5rem' }}>
              Linked Video Assets
            </div>

            {post.videos && post.videos.map((video, index) => (
              <div key={index} style={{ marginBottom: '1.5rem' }}>
                {video.youtubeUrl ? (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                      cursor: 'pointer',
                    }}
                    onClick={() => openModal('video', video.youtubeUrl)}
                  >
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      style={{ width: '100%', borderRadius: '8px' }}
                    />
                    <PlayCircle
                      size={48}
                      color="#fff"
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.6))',
                      }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      position: 'relative',
                      width: '100%',
                      borderRadius: '8px',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      style={{ width: '100%', borderRadius: '8px' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: '1rem',
                      }}
                    >
                      <Hourglass size={36} />
                      In Progress
                    </div>
                  </div>
                )}
                <h3 style={{ fontSize: '1rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {video.title}
                </h3>
                <p style={{ fontSize: '0.85rem', color: '#444' }}>
                  Runtime: {video.length}. Click image to preview.
                </p>
              </div>
            ))}
          </div>

          {/* RIGHT: Blog Detail and Assets */}
          <div style={{ flex: '2 1 400px', minWidth: '300px' }}>
            <h1 style={{ marginBottom: '0.5rem' }}>{post.title}</h1>
            <p style={{ whiteSpace: 'pre-line', marginBottom: '1rem', color: '#555', fontSize: '1rem' }}>{post.description}</p>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {/* Blog Cover */}
              <div>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem', color: '#888' }}>
                  Blog Cover and social sharing image
                </div>
                <img
                  src={post.coverImage || '/assets/blog-cover-placeholder.png'}
                  alt="Blog Cover"
                  style={{
                    width: '200px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                  }}
                  onClick={() => openModal('image', post.coverImage || '/assets/blog-cover-placeholder.png')}
                />
              </div>

              {/* Blog CTA Banner */}
              <div>
                <div style={{ fontSize: '0.8rem', marginBottom: '0.5rem', color: '#888' }}>
                  Blog Banner with CTA to Lead Gen Funnel
                </div>
                <img
                  src={post.ctaImage || '/assets/blog-cta-placeholder.png'}
                  alt="CTA Banner"
                  style={{
                    width: '280px',
                    borderRadius: '6px',
                    border: '1px solid #ccc',
                    cursor: 'pointer',
                  }}
                  onClick={() => openModal('image', post.ctaImage || '/assets/blog-cta-placeholder.png')}
                />
              </div>
            </div>
          </div>
        </div>
      ))}

      {renderModal()}
    </div>
  );
};

export default Blog;